import axios from 'axios'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'

interface SharedItem {
  id: string
  hash: string
  name: string
  type: 'file' | 'folder'
  mimeType?: string
  size?: number
  url?: string
  items?: SharedItem[]
}

// Mock data for shared items
const mockSharedItems: Record<string, SharedItem> = {
  'aB3d9F7': {
    id: '4',
    hash: 'aB3d9F7',
    name: 'Project Proposal.pdf',
    type: 'file',
    mimeType: 'application/pdf',
    size: 1024 * 1024 * 2.5, // 2.5 MB
    url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
  },
  'xY7q2Z8': {
    id: '2',
    hash: 'xY7q2Z8',
    name: 'Images',
    type: 'folder',
    items: [
      {
        id: '7',
        hash: '',
        name: 'Photo1.jpg',
        type: 'file',
        mimeType: 'image/jpeg',
        size: 1024 * 1024 * 1.2, // 1.2 MB
        url: 'https://images.unsplash.com/photo-1682687982501-1e58ab814714'
      },
      {
        id: '8',
        hash: '',
        name: 'Photo2.jpg',
        type: 'file',
        mimeType: 'image/jpeg',
        size: 1024 * 1024 * 0.8, // 0.8 MB
        url: 'https://images.unsplash.com/photo-1682687982501-1e58ab814714'
      }
    ]
  }
}

// Initialize global mockSharedItems for dynamic updates
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.mockSharedItems = mockSharedItems;
}

// Get a shared item by hash
export const getSharedItem = async (hash: string): Promise<SharedItem | null> => {
  // In a real app, this would be an API call
  return new Promise((resolve) => {
    setTimeout(() => {
      // Check both our static mockSharedItems and any dynamically added ones
      // @ts-ignore
      const dynamicItems = typeof window !== 'undefined' ? window.mockSharedItems || {} : {};
      resolve(dynamicItems[hash] || mockSharedItems[hash] || null)
    }, 500)
  })
}

// Download a shared item
export const downloadSharedItem = async (hash: string, path?: string): Promise<void> => {
  // @ts-ignore
  const dynamicItems = typeof window !== 'undefined' ? window.mockSharedItems || {} : {};
  const item = dynamicItems[hash] || mockSharedItems[hash];
  
  if (!item) throw new Error('Item not found')
  
  if (item.type === 'file' && item.url) {
    // For a single file, just download it directly
    saveAs(item.url, item.name)
    
    // Increment download count
    await incrementDownloadCount(hash)
  } else if (item.type === 'folder' && item.items) {
    // For a folder, create a zip file with all contents
    const zip = new JSZip()
    
    // If path is provided, only include items in that path
    let itemsToInclude = item.items
    if (path && path !== '/') {
      // This is a simplified version - in a real app, you'd need to traverse the folder structure
      // based on the path
      itemsToInclude = []
    }
    
    // Add files to the zip
    for (const file of itemsToInclude) {
      if (file.type === 'file' && file.url) {
        try {
          const response = await fetch(file.url)
          const blob = await response.blob()
          zip.file(file.name, blob)
        } catch (error) {
          console.error(`Failed to add ${file.name} to zip:`, error)
        }
      }
    }
    
    // Generate and download the zip
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    saveAs(zipBlob, `${item.name}.zip`)
    
    // Increment download count
    await incrementDownloadCount(hash)
  }
}

// Increment view count for a shared item
export const incrementViewCount = async (hash: string): Promise<void> => {
  // In a real app, this would be an API call
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, 200)
  })
}

// Increment download count for a shared item
export const incrementDownloadCount = async (hash: string): Promise<void> => {
  // In a real app, this would be an API call
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, 200)
  })
}